import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField } from '@/components/forms';
import { useCursorSelect } from '@/hooks/use-cursor-select';
import { appTzLabel, fromDateRange, isAfterAppToday, toDayjsRange } from '@/lib/date';

import { listReplacementCustomers, listReplacementDevices } from '../api';
import { toDefaults, toFilters } from '../filters';
import { REPLACEMENT_REASONS, REPLACEMENT_STATUS_CHIP } from '../format';
import { replacementKeys, useReplacementCategories, useReplacementCities } from '../hooks';
import { replacementFiltersSchema } from '../schemas';
import type {
  ReplacementFilterFormValues,
  ReplacementListFilters,
  ReplacementOption,
  ReplacementReason,
  ReplacementStatus,
} from '../types';

import styles from './replacement-filter-drawer.module.scss';

const REASON_OPTIONS = (Object.keys(REPLACEMENT_REASONS) as ReplacementReason[]).map((value) => ({
  value,
  label: REPLACEMENT_REASONS[value],
}));

/** Pending / Approved / Rejected — labels reuse the table's status chip map. */
const STATUS_OPTIONS = (Object.keys(REPLACEMENT_STATUS_CHIP) as ReplacementStatus[]).map(
  (value) => ({ value, label: REPLACEMENT_STATUS_CHIP[value].label }),
);

const toOption = (o: ReplacementOption) => ({ value: o.id, label: o.name });

export type ReplacementFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: ReplacementListFilters;
  onApply: (filters: ReplacementListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<ReplacementFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const draft = toDefaults(value);
  const form = useForm<ReplacementFilterFormValues>({
    resolver: zodResolver(replacementFiltersSchema),
    defaultValues: {
      ...draft,
      orderDateFrom: draft.orderDateFrom ?? null,
      orderDateTo: draft.orderDateTo ?? null,
      replacementDateFrom: draft.replacementDateFrom ?? null,
      replacementDateTo: draft.replacementDateTo ?? null,
    },
    mode: 'onBlur',
  });

  const customerSelect = useCursorSelect({
    queryKey: replacementKeys.metaCustomers,
    fetchPage: async ({ search, cursor, limit }) => {
      const page = await listReplacementCustomers({ search, cursor, limit });
      return {
        data: page.data,
        nextCursor: page.meta.nextCursor,
        hasNextPage: page.meta.hasNextPage,
      };
    },
    toOption,
  });
  const deviceSelect = useCursorSelect({
    queryKey: replacementKeys.metaDevices,
    fetchPage: async ({ search, cursor, limit }) => {
      const page = await listReplacementDevices({ search, cursor, limit });
      return {
        data: page.data,
        nextCursor: page.meta.nextCursor,
        hasNextPage: page.meta.hasNextPage,
      };
    },
    toOption,
  });

  const cities = useReplacementCities();
  const cityOptions = useMemo(
    () => (cities.data ?? []).map((c) => ({ value: c, label: c })),
    [cities.data],
  );
  const categories = useReplacementCategories();
  const categoryOptions = useMemo(
    () => (categories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [categories.data],
  );

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  // One DateRangePicker per range (Figma 30:413888) bound to the two draft fields; keeps the
  // From<=To guard + Apply-disable, though the range picker itself blocks an inverted pick.
  const [orderedFrom, orderedTo, requestedFrom, requestedTo] = useWatch({
    control: form.control,
    name: ['orderDateFrom', 'orderDateTo', 'replacementDateFrom', 'replacementDateTo'],
  });
  const orderedError = form.formState.errors.orderDateTo?.message;
  const requestedError = form.formState.errors.replacementDateTo?.message;

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={464}
      primaryText="Apply"
      onPrimary={handleApply}
      primaryDisabled={Boolean(orderedError || requestedError)}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <MultiSelectField
        name="customerIds"
        control={form.control}
        label="Customer"
        placeholder="Select"
        showSearch={customerSelect.showSearch}
        loading={customerSelect.loading}
        onPopupScroll={customerSelect.onPopupScroll}
        options={customerSelect.options}
      />

      <MultiSelectField
        name="deviceIds"
        control={form.control}
        label="Device"
        placeholder="Select"
        showSearch={deviceSelect.showSearch}
        loading={deviceSelect.loading}
        onPopupScroll={deviceSelect.onPopupScroll}
        options={deviceSelect.options}
      />

      <MultiSelectField
        name="categoryIds"
        control={form.control}
        label="Device Category"
        placeholder="Select"
        loading={categories.isLoading}
        options={categoryOptions}
      />

      <MultiSelectField
        name="cities"
        control={form.control}
        label="City"
        placeholder="Select"
        loading={cities.isLoading}
        options={cityOptions}
      />

      {/* BA-added after the first build (story 110171 comment): the AC filter list now carries
          Replacement Status between City and Reason. */}
      <MultiSelectField
        name="statuses"
        control={form.control}
        label="Replacement Status"
        placeholder="Select"
        options={STATUS_OPTIONS}
      />

      <MultiSelectField
        name="reasons"
        control={form.control}
        label="Reason for Replacement"
        placeholder="Select"
        options={REASON_OPTIONS}
      />

      {/* One DateRangePicker per range (Figma 30:413888), bound to the two scalar draft fields —
          mirrors the shipped installation filter drawer. */}
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{appTzLabel('Order Date Range')}</legend>
        <DateRangePicker
          aria-label={appTzLabel('Order Date Range')}
          format="MM/DD/YYYY"
          disabledDate={isAfterAppToday}
          style={{ width: '100%' }}
          status={orderedError ? 'error' : undefined}
          value={toDayjsRange(orderedFrom && orderedTo ? [orderedFrom, orderedTo] : null)}
          onChange={(range) => {
            const iso = fromDateRange(range);
            form.setValue('orderDateFrom', iso?.[0] ?? null, { shouldValidate: true });
            form.setValue('orderDateTo', iso?.[1] ?? null, { shouldValidate: true });
          }}
        />
        {orderedError ? <p className={styles.error}>{orderedError}</p> : null}
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{appTzLabel('Replacement Request Date Range')}</legend>
        <DateRangePicker
          aria-label={appTzLabel('Replacement Request Date Range')}
          format="MM/DD/YYYY"
          disabledDate={isAfterAppToday}
          style={{ width: '100%' }}
          status={requestedError ? 'error' : undefined}
          value={toDayjsRange(requestedFrom && requestedTo ? [requestedFrom, requestedTo] : null)}
          onChange={(range) => {
            const iso = fromDateRange(range);
            form.setValue('replacementDateFrom', iso?.[0] ?? null, { shouldValidate: true });
            form.setValue('replacementDateTo', iso?.[1] ?? null, { shouldValidate: true });
          }}
        />
        {requestedError ? <p className={styles.error}>{requestedError}</p> : null}
      </fieldset>
    </Drawer>
  );
}

export function ReplacementFilterDrawer({ open, ...rest }: ReplacementFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
